// components/RecorridoCompra.js — sensor-recorrido NIVEL 2 (2-oct-2026). La línea de tiempo
// de la compra bajo «Origen de la compra», en las DOS superficies de la ficha (drawer de
// Pedidos y modal de Caja — la lección del 30-sep: una sola queda coja). Fetch propio a
// GET /api/orders/:id/recorrido (admin): para un rol sin acceso el bloque NO EXISTE (403 →
// null, sin ruido) — distinto del error de consulta, que sí se dice. «Sin recorrido» llega
// con sabor del backend y se traduce acá; un sabor desconocido se muestra CRUDO (regla del
// nivel 1: el riesgo es ocultar). El payload jamás trae el id de sesión (test R2 backend).
import React, { useState, useEffect } from 'react';
import { colDateTimeStr } from '../utils/colTimeUtils';
import { fuenteTexto } from './OrigenCompra'; // UNA definición de fuentes, no dos (sensor-contexto)

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const SABORES_SIN_RECORRIDO = {
  pos: 'Compra en el local o creada a mano: sin recorrido web.',
  anterior_al_sensor: 'Compra anterior al sensor de recorridos: no hay línea de tiempo.',
  sin_emision: 'La visita de esta compra no quedó medida (el navegador del comprador no emitió): sin recorrido.',
  crudos_expirados: 'El recorrido ya no se puede reconstruir: los eventos expiraron antes de guardarse.',
};
export const PASOS = {
  vista_producto: 'Vio',
  agregar_carrito: 'Agregó al carrito',
  inicio_checkout: 'Inició el pago de',
};

const horaTienda = (iso) => {
  const s = colDateTimeStr(iso);
  return s ? s.slice(11) : '';
};

// sensor-contexto: la línea «Llegó desde…» de ESA sesión. La fuente llega CRUDA
// (utm_source gana sobre referrer_host); lo no mapeado se muestra crudo. Sin contexto
// (sesión anterior al 1.3.0): null — la línea no existe, no se inventa.
export const lineaContexto = (c) => {
  if (!c) return null;
  // sensor-contexto-2: la clasificación viene del BACKEND (fuente_cruda, la misma de los
  // cortes): un referrer propio llega como '(directo)' y acá jamás se pinta como fuente.
  // sensor-contexto-3: '(interna)' = el referrer era la propia tienda — la entrada real
  // no se midió; se dice, no se pinta como fuente ni como «directa».
  if (c.fuente_cruda === '(interna)') {
    const d2 = { celular: 'celular', tableta: 'tableta', computador: 'computador' }[c.dispositivo] || c.dispositivo;
    return `Llegó desde la propia tienda (entrada sin medir)${d2 ? ` · ${d2}` : ''}`;
  }
  const crudo = c.fuente_cruda !== undefined
    ? (c.fuente_cruda === '(directo)' || c.fuente_cruda === '(sin contexto)' ? '' : c.fuente_cruda)
    : (c.utm_source || c.referrer_host); // payload viejo sin fuente_cruda: forma 1.3.0
  const fuente = crudo ? (fuenteTexto(crudo) || crudo) : 'visita directa';
  const disp = { celular: 'celular', tableta: 'tableta', computador: 'computador' }[c.dispositivo] || c.dispositivo;
  return `Llegó desde ${fuente}${disp ? ` · ${disp}` : ''}`;
};

export default function RecorridoCompra({ orderId, variante = 'modal' }) {
  const [st, setSt] = useState({ estado: 'cargando' });
  useEffect(() => {
    if (!orderId) return undefined;
    let cancelled = false;
    setSt({ estado: 'cargando' });
    fetch(`${API_URL}/api/orders/${orderId}/recorrido`)
      .then((res) => {
        // Función de admin: sin permiso, el bloque no existe (no es un error que mostrar).
        if (res.status === 401 || res.status === 403) return { oculto: true };
        return res.json().then((data) => {
          if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
          return { data };
        });
      })
      .then((r) => {
        if (cancelled) return;
        setSt(r.oculto ? { estado: 'oculto' } : { estado: 'ok', data: r.data });
      })
      .catch(() => { if (!cancelled) setSt({ estado: 'error' }); });
    return () => { cancelled = true; };
  }, [orderId]);

  if (st.estado === 'oculto') return null;

  let cuerpo;
  if (st.estado === 'cargando') {
    cuerpo = <div className="odm-addr-line">Cargando…</div>;
  } else if (st.estado === 'error') {
    // Fuente caída ≠ «sin recorrido»: no se afirma nada sobre la compra.
    cuerpo = <div className="odm-addr-line">No se pudo consultar el recorrido de esta compra.</div>;
  } else {
    const d = st.data || {};
    if (d.sin_recorrido) {
      cuerpo = (
        <div className="odm-addr-line">
          {SABORES_SIN_RECORRIDO[d.sin_recorrido] || `Sin recorrido (${d.sin_recorrido}).`}
        </div>
      );
    } else if (d.recorrido && (d.recorrido.eventos || []).length > 0) {
      const r = d.recorrido;
      cuerpo = (
        <>
          {lineaContexto(r.contexto) && (
            <div className="odm-addr-line">{lineaContexto(r.contexto)}</div>
          )}
          <div className="odm-addr-line">
            Empezó a las {horaTienda(r.eventos[0].ts)} (hora de tu tienda)
            {' · '}{r.duracion_min > 0 ? `${r.duracion_min} min en total` : 'menos de un minuto'}
          </div>
          {r.eventos.map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="odm-addr-line" key={`${e.ts}-${i}`}>
              {i > 0 && e.minutos_desde_anterior > 0 ? `${e.minutos_desde_anterior} min después · ` : ''}
              {PASOS[e.tipo] || e.tipo} {e.producto && e.producto.nombre ? e.producto.nombre : `producto ${e.producto ? e.producto.id : '?'}`}
            </div>
          ))}
          {r.origen === 'crudos' && (
            <div className="odm-addr-line">Recorrido de hoy, computado al momento: se guarda esta madrugada.</div>
          )}
        </>
      );
    } else {
      cuerpo = <div className="odm-addr-line">Esta compra no trae recorrido medido.</div>;
    }
  }

  if (variante === 'drawer') {
    return (
      <>
        <h4 className="op-insp-h">Recorrido de la compra</h4>
        <div className="op-origen">{cuerpo}</div>
      </>
    );
  }
  return (
    <div className="odm-section">
      <div className="odm-section-title">Recorrido de la compra</div>
      <div className="odm-addr-block">{cuerpo}</div>
    </div>
  );
}
